import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, useMemo } from 'react';

import type { LineItem } from '@/types';

import PaginatedList from '../PaginatedList';
import OrderLineItem from './OrderLineItem';

export const TEST_ID = 'order-items';
export const QUANTITY_TEST_ID = 'order-item-quantity';
const CONDENSED_THRESHOLD = 4;

export interface OrderItemsProps extends ComponentPropsWithoutRef<'div'> {
  readonly lineItems: LineItem[];
}

const OrderItems = ({ lineItems, ...props }: OrderItemsProps) => {
  const t = useTranslations();
  const { isCondensed, pb, pt, gap } = useMemo(() => {
    const isCondensed = lineItems.length > CONDENSED_THRESHOLD;

    // padding class names (so we don't concatenate strings)
    const styles = isCondensed
      ? { pb: 'pb-3', pt: 'pt-3', gap: 'gap-3' }
      : { pb: 'pb-5', pt: 'pt-5', gap: 'gap-5' };

    return {
      ...styles,
      isCondensed,
    };
  }, [lineItems.length]);

  return (
    <div data-testid={TEST_ID} {...props}>
      <PaginatedList className={gap} list={lineItems}>
        {(lineItem: LineItem, idx: number, { length }) => {
          const isFirst = !idx;
          const isLast = idx + 1 === length;

          return (
            <div
              className={cx('flex items-center', {
                [`${pt} border-t border-gray-300`]: !isFirst,
                [`${pb}`]: !isLast,
              })}
              key={lineItem.image.src + idx} // must use index because item details are often the same
            >
              <OrderLineItem lineItem={lineItem} condensed={isCondensed} />

              {lineItem.quantity && lineItem.quantity > 1 ? (
                <Typography
                  Element="span"
                  className="shrink-0"
                  data-testid={QUANTITY_TEST_ID}
                >
                  {t('eop_quantity_text', { number: lineItem.quantity })}
                </Typography>
              ) : null}
            </div>
          );
        }}
      </PaginatedList>
    </div>
  );
};

export default OrderItems;
